import * as THREE from "three";
import { BufferGeometry, Line, Line3, MathUtils, Ray, Vector3 } from "three";
import { generateUUID } from "three/src/math/MathUtils";
import { ArcDimension } from "@/Dimensions/ArcDimension";
import { LineDimension } from "@/Dimensions/LineDimension";
import { IndserObject, IndsertPosition } from "@/core/IndserObject";
import { MLayer } from "@/core/MLayer";
import {
  DBEntity,
  DbLayer,
  DbLine,
  FMathUtils,
  ModelManager,
  ViewManager,
} from "@/CadEngine";
import { BPMathUtils } from "@/utils/BPMathUtils";
import {
  DrawOperateState,
  IDrawOperation,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
} from "../IDrawOperation";
import { AnchorPointAux } from "@/Dimensions/AnchorPointAuxs";
import { BPLinear } from "@/core/BPLinear";
import { OrthogonalAux } from "@/Dimensions/OrthogonalAux";
import { Context } from "@/core/context";
import {
  DrawOperateLineParams,
  ArcLayoutType,
  CommonColor,
  ActiveInputType,
  CircleArcInfo,
} from "@/core/Parameters";
import { DimensionGroup } from "@/core/DimensionGroup";
import { ProtectRadius } from "./ProtectRadius";
import { ULabel } from "@/ui/ULabel";

class DrawArcLayout {
  private m_model: ModelManager;
  private m_layer: DbLayer;
  private m_auxLayer: MLayer;
  private m_material: THREE.LineBasicMaterial;
  private m_tempObject: THREE.Line | undefined;
  private m_auxGeometry: THREE.BufferGeometry | undefined;
  private points: THREE.Vector3[] = [];
  private m_locationObject: THREE.Line;
  private m_size = { x: 1, y: 1, z: 1 };
  private view: ViewManager;
  private m_dimension: DimensionGroup;
  private inputValue: string = "";
  private m_auxPoints: {
    orgin: THREE.Vector3;
    dir: THREE.Vector3 | undefined;
  }[] = [];
  private m_preMovePoint: { point: THREE.Vector3; time: number } = {
    point: new THREE.Vector3(),
    time: 0,
  };
  private m_auxOrthogonalOrVertical:
    | { position: IndsertPosition; origin: THREE.Vector3; dir: THREE.Vector3 }
    | undefined;

  private arcGeometry: BufferGeometry;
  public get Points() {
    return this.points;
  }
  private specialParams?: any;
  private m_activeInputType: ActiveInputType = ActiveInputType.None;
  private m_inputValue: string = "";
  private m_distanceInputValue: string = "";
  private m_angleInputValue: string = "";
  private m_quadraturePoint: THREE.Vector3 | undefined = undefined;
  private m_isEnter: boolean = false;
  private m_arcOptions: CircleArcInfo = {
    center: new THREE.Vector3(),
    radius: 0,
    startAngle: 0,
    endAngle: 0,
    clockwise: true,
  };
  private m_color: number;
  private ProtectRadiusGroup: THREE.Group;
  private m_arcPointsNum: number = 49;
  private m_signModelPositions: THREE.Vector3[] = [];
  private m_context: Context;
  private m_TextLabel: ULabel;
  private m_isShift: Boolean = false;
  constructor(
    context: Context,
    layer: DbLayer,
    color: number,
    specialParams?: any
  ) {
    this.m_context = context;
    this.m_model = context.ModelManager;
    this.m_layer = layer;
    this.m_color = color;
    this.m_auxLayer = context.SceneManager.AuxModel;
    this.m_material = new THREE.LineBasicMaterial({
      color: new THREE.Color(CommonColor.White),
    });

    this.m_locationObject = context.ToolsManager.CreatedLocationLine();

    this.view = context.ViewManager;
    this.m_dimension = new DimensionGroup(this.view, this.m_auxLayer);
    this.m_dimension.Line.SetVaule("");
    this.m_dimension.Arc.SetVaule("");
    this.arcGeometry = new THREE.BufferGeometry();
    this.arcLine = new THREE.LineSegments(this.arcGeometry, this.m_material);
    this.m_auxLayer.add(this.arcLine);
    this.AuxPointGroup = new THREE.Group();
    this.m_auxLayer.add(this.AuxPointGroup);
    this.ProtectRadiusGroup = new THREE.Group();
    this.m_auxLayer.add(this.ProtectRadiusGroup);
    this.specialParams = specialParams;
    this.m_TextLabel = new ULabel(
      this.m_context.ViewManager,
      this.m_context.Dom
    );
    this.m_TextLabel.Visibility = true;
  }

  onMouseMove(e: PointerEvent): void {}
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.keyCode == 16) {
      this.m_isShift = true;
    }
    if (!this.points.length || this.points.length > 2) return;
    this.m_auxPoints = [];
    if (e.key === "Tab") {
      this.m_inputValue = "";
      if (this.m_activeInputType == ActiveInputType.None) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      } else if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_activeInputType = ActiveInputType.Angle;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #ffffff";
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      }
    }
    if (e.key == "Backspace") {
      if (this.m_inputValue.length > 0) {
        this.m_inputValue = this.m_inputValue.substring(
          0,
          this.m_inputValue.length - 1
        );
      }
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_distanceInputValue = this.m_inputValue;
        }
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_angleInputValue = this.m_inputValue;
        }
      }
    } else if ((e.key >= "0" && e.key <= "9") || e.key == ".") {
      if (this.m_activeInputType == ActiveInputType.None) return;
      if (this.m_inputValue?.includes(".") && e.key == ".") return;
      this.m_inputValue += e.key;
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        this.m_distanceInputValue = this.m_inputValue;
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue);
        this.m_angleInputValue = this.m_inputValue;
      }
    } else if (e.key == "Enter") {
      let length = parseFloat(this.m_distanceInputValue) * 0.001;
      let position = this.m_locationObject.position.clone();
      let curDir = position.clone().sub(this.points[0]).normalize();
      let angle = curDir.angleTo(new THREE.Vector3(1, 0, 0));
      if (curDir.clone().cross(new THREE.Vector3(1, 0, 0)).z > 0) {
        angle = -angle;
      }
      curDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), -angle);
      curDir.applyAxisAngle(
        new THREE.Vector3(0, 0, 1),
        MathUtils.degToRad(parseFloat(this.m_angleInputValue))
      );
      position = this.points[0].clone().add(curDir.multiplyScalar(length));
      this.m_isEnter = true;
      this.m_quadraturePoint = undefined;
      this.OnMovePoint({
        point: position,
        geo: undefined,
        position: IndsertPosition.None,
      });
      this.m_isEnter = false;
      this.points.push(position);
      this.view.Render();
    }
  }
  onKeyUp(e: KeyboardEvent): void {
    if (e.keyCode == 16) {
      this.m_isShift = false;
    }
  }

  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private get curDir() {
    return this.m_curPoint.clone().sub(this.points[0]).normalize();
  }
  OnZoom(camera: THREE.Camera) {
    this.m_auxPoints = [];
    let scale = this.scale;
    if (this.points.length > 0) {
      this.m_dimension.Line.SetDimension(
        this.points[0],
        this.m_curPoint,
        scale
      );
      this.m_dimension.Arc.SetDimension(
        this.points[0],
        new THREE.Vector3(1, 0, 0),
        this.curDir,
        scale
      );
    }
    this.m_dimension.Anchor.SetPoint(this.auxPoint, scale);
    this.m_dimension.OrthogonalAux.visible = false;
    if (this.m_auxOrthogonalOrVertical) {
      this.m_dimension.OrthogonalAux.Set(
        this.m_auxOrthogonalOrVertical.origin,
        this.m_auxOrthogonalOrVertical.dir,
        scale,
        this.m_auxOrthogonalOrVertical.position == IndsertPosition.AuxOrthogonal
          ? undefined
          : this.m_curPoint
      );
    }
  }
  OnWheel(e?: WheelEvent): void {
    if (this.points.length) {
      this.m_dimension.Line.SetDimension(
        this.points[0],
        this.m_curPoint,
        this.scale
      );
      let xAxis = new THREE.Vector3(1, 0, 0);

      this.m_dimension.Arc.SetDimension(
        this.points[0],
        xAxis,
        this.curDir,
        this.scale
      );
    }
  }
  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(params: DrawOperationMovePointParams): void {
    this.view.m_context.Dom.style.cursor = "none";
    let xAxis = new THREE.Vector3(1, 0, 0);
    this.inputValue = "";
    this.m_locationObject.position.copy(params.point);
    this.m_locationObject.visible = true;
    let TextLabelLocation = new THREE.Vector3();
    TextLabelLocation.x = params.point.x + this.scale * 35;
    TextLabelLocation.y = params.point.y - this.scale * 10;
    this.m_TextLabel.SetVaule(
      "长按Shift锁定正交",
      TextLabelLocation,
      new THREE.Vector3(1, 0, 0)
    );
    if (this.points.length >= 3) {
      this.OnWheel();
      return;
    }
    this.m_curPoint = params.point;
    if (this.points.length == 0) return;
    let ps = [];
    ps.push(...this.points);
    ps.push(params.point);

    if (this.m_auxGeometry) {
      if (ps.length == 4) {
        ps.pop();
      }
      if (
        (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
        !this.m_isEnter
      ) {
        this.m_curPoint =
          this.view.m_context.ToolsManager.GetOrthogonalityPoint(
            params.point,
            this.points[0]
          );
      }
      this.m_auxGeometry.setFromPoints([ps[0], this.m_curPoint]);
      this.m_tempObject!.computeLineDistances();
    } else {
      this.m_tempObject = this.m_model.m_context.ToolsManager.CreatedAuxLine();
      this.m_auxGeometry = this.m_tempObject.geometry;
    }
    this.m_dimension.Arc.SetDimension(
      this.points[0],
      new THREE.Vector3(1, 0, 0),
      this.curDir,
      this.scale
    );

    this.m_dimension.Line.SetDimension(ps[0], this.m_curPoint, this.scale);
    if (ps.length === 3) {
      // this.executeArcAlgorithm(ps[0], ps[1], this.m_curPoint);
      this.computerCircleArc();
    }
    let angle = this.curDir.clone().angleTo(xAxis);
    if (this.curDir.clone().cross(xAxis).z > 0) {
      angle = Math.PI * 2 - angle;
    }
    angle = (angle / Math.PI) * 180;
    let distance = (this.m_curPoint.distanceTo(this.points[0]) * 1000)
      .toFixed(2)
      .toString();
    this.m_distanceInputValue = distance;
    this.m_angleInputValue = angle.toFixed(0).toString();
    this.m_activeInputType = ActiveInputType.None;
    this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
    this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
    this.m_inputValue = "";
  }
  OnDownPoint(params: DrawOperationMovePointParams): void {
    let { point } = params;
    if (this.points.length <= 2) {
      if (
        (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
        params.position == "none"
      ) {
        point = this.m_curPoint.clone();
      } else {
        this.m_curPoint.copy(point);
      }
      this.points.push(point);
    }
  }
  GetPoints(): Vector3[] {
    return this.points;
  }

  get auxPoint() {
    let ps: THREE.Vector3[] = [];
    this.m_auxPoints.forEach((x) => {
      ps.push(x.orgin);
    });
    return ps;
  }

  Finish(operate: DrawOperateState) {
    this.clear();
    if (operate == DrawOperateState.complete) {
      if (this.points.length >= 2) {
        let addLines: DbLine[] = [];
        if (this.specialParams.isConnectingLine) {
          addLines = this.addLines();
        }
        let returnLine: {
          center: { x: number; y: number; z: number };
          matrixs: THREE.Matrix[] | undefined;
          isClockwise: boolean;
          lines: DbLine[];
        } = {
          center: this.m_arcOptions.center,
          matrixs: this.SignMatrixs,
          isClockwise: this.m_arcOptions.clockwise,
          lines: addLines,
        };

        return returnLine;
      }
    } else if (operate == DrawOperateState.cancel) {
    }
    return [];
  }
  private clear() {
    this.view.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_tempObject!,
      this.arcLine!,
    ]);
    this.view.m_context.ToolsManager.GroupClear(this.AuxPointGroup);
    this.view.m_context.ToolsManager.GroupClear(this.ProtectRadiusGroup);
    this.m_dimension?.dispose();
    this.m_TextLabel?.dispose();
  }
  private get scale() {
    return this.view.ScreenModelScale;
  }

  private computerCircleArc() {
    let xAxis = new THREE.Vector3(1, 0, 0);
    let zAxis = new THREE.Vector3(0, 0, 1);
    let vect1 = this.points[1].clone().sub(this.points[0]).normalize();
    let center1StartP = new THREE.Vector3().lerpVectors(
      this.points[0],
      this.points[1],
      0.5
    );
    let center1EndP = center1StartP
      .clone()
      .add(vect1.clone().applyAxisAngle(zAxis, Math.PI / 2));
    let line1: any = {
      points: [
        center1StartP.x,
        center1StartP.y,
        center1StartP.z,
        center1EndP.x,
        center1EndP.y,
        center1EndP.z,
      ],
    };

    let vect2 = this.m_curPoint.clone().sub(this.points[1]).normalize();

    let center2StartP = new THREE.Vector3().lerpVectors(
      this.points[1],
      this.m_curPoint,
      0.5
    );
    let center2EndP = center2StartP
      .clone()
      .add(vect2.clone().applyAxisAngle(zAxis, Math.PI / 2));
    let line2: any = {
      points: [
        center2StartP.x,
        center2StartP.y,
        center2StartP.z,
        center2EndP.x,
        center2EndP.y,
        center2EndP.z,
      ],
    };
    let center = FMathUtils.GetLineIntersection(
      line1 as DbLine,
      line2 as DbLine
    );
    if (!center) return undefined;
    let radius = this.points[0].distanceTo(center);
    let radiusVector1 = new THREE.Vector3()
      .subVectors(this.points[0], center)
      .normalize();
    let radiusVector2 = new THREE.Vector3()
      .subVectors(this.points[1], center)
      .normalize();
    let startAngle = radiusVector1.clone().angleTo(xAxis);
    let endAngle = radiusVector2.clone().angleTo(xAxis);
    let clockwise = true;
    let crossResult = Math.abs(vect2.clone().cross(vect1).z);
    if (crossResult < 0.001) {
      return undefined;
    } else {
      if (vect2.clone().cross(vect1).z > 0) {
        clockwise = false;
      }
      if (radiusVector1.clone().cross(xAxis).z > 0) {
        startAngle = Math.PI * 2 - startAngle;
      }
      if (radiusVector2.clone().cross(xAxis).z > 0) {
        endAngle = Math.PI * 2 - endAngle;
      }
      this.m_arcOptions = {
        center,
        radius,
        startAngle,
        endAngle,
        clockwise: clockwise,
      };
      this.updateArc();
    }
  }

  private arcLine: THREE.Line | undefined;

  // 创建弧线
  private arcLength: number = 0;
  private Arc: THREE.ArcCurve | undefined;
  private arcPoints: THREE.Vector2[] = []; //弧线点集合
  private updateArc() {
    this.Arc = new THREE.ArcCurve(
      this.m_arcOptions.center.x,
      this.m_arcOptions.center.y,
      this.m_arcOptions.radius,
      this.m_arcOptions.startAngle,
      this.m_arcOptions.endAngle,
      this.m_arcOptions.clockwise
    );
    this.arcPoints = this.Arc.getPoints(this.m_arcPointsNum);
    this.arcLength = this.Arc.getLength() * 1000;
    if (this.AuxPointGroup.children.length > 0) {
      this.view.m_context.ToolsManager.GroupClear(this.AuxPointGroup);
      this.AuxPointGroup = new THREE.Group();
      this.m_auxLayer.add(this.AuxPointGroup);
    }
    if (this.ProtectRadiusGroup.children.length > 0) {
      this.view.m_context.ToolsManager.GroupClear(this.ProtectRadiusGroup);
      this.ProtectRadiusGroup = new THREE.Group();
      this.m_auxLayer.add(this.ProtectRadiusGroup);
    }

    this.SignMatrixs.length = 0;
    this.m_signModelPositions.length = 0;
    let segmentNum = Math.floor(this.arcLength / this.specialParams.interval);

    for (let i = 0; i <= segmentNum; i++) {
      let ratio = (this.specialParams.interval * i) / this.arcLength;
      let point = this.Arc.getPointAt(ratio);
      let vec3 = new THREE.Vector3(point.x, point.y, 0);
      this.m_signModelPositions.push(vec3);
      this.createdAuxPoint(vec3);
    }

    for (let i = 0; i < this.arcPoints.length; i++) {
      if (i > 1) {
        if (i % 2 == 0) {
          this.arcPoints.splice(i, 1, this.arcPoints[i - 1], this.arcPoints[i]);
        }
      }
    }

    for (let i = 0; i < this.m_signModelPositions.length; i++) {
      this.createdProtectRadius(this.m_signModelPositions[i]);
    }

    this.arcGeometry.setFromPoints(this.arcPoints);
    this.arcGeometry!.attributes.position.needsUpdate = true;
    this.arcGeometry.computeBoundingBox();
    this.arcGeometry.computeBoundingSphere();
  }
  // 保护半径
  private createdProtectRadius(position: THREE.Vector3) {
    if (this.specialParams.protectRadius.isCreate === false) {
      return;
    }
    let protectRadiusProject = new ProtectRadius(
      this.view.m_context,
      this.specialParams.protectRadius.radius
    ).ProtectRadiusProject;
    protectRadiusProject.position.copy(position);
    this.ProtectRadiusGroup.add(protectRadiusProject);
  }

  // 辅助标志
  private AuxPointGroup: THREE.Group = new THREE.Group();
  private SignMatrixs: THREE.Matrix4[] = [];
  private createdAuxPoint(position: THREE.Vector3) {
    let geo = new THREE.BufferGeometry();
    let positions = new Float32Array(37 * 3);
    for (let index = 0; index <= 36; index++) {
      let angle = (Math.PI * 2 * index) / 36;
      let x = Math.cos(angle) * 0.2;
      let y = Math.sin(angle) * 0.2;
      let z = 0;
      positions[index * 3] = x;
      positions[index * 3 + 1] = y;
      positions[index * 3 + 2] = z;
    }
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    let material = new THREE.LineBasicMaterial({ color: CommonColor.White });
    let AuxPoint = new THREE.Line(geo, material);
    AuxPoint.position.copy(position);
    this.AuxPointGroup.add(AuxPoint);
    let matrix = new THREE.Matrix4();
    let v1 = new THREE.Vector3().subVectors(this.m_arcOptions.center, position);
    let xAxis = new THREE.Vector3(1, 0, 0);
    let zAxis = new THREE.Vector3(0, 0, 1);
    let cross = new THREE.Vector3().crossVectors(v1, xAxis);
    let angle = v1.angleTo(xAxis);
    if (cross.z > 0) {
      angle = Math.PI * 2 - angle;
    }
    angle = angle + Math.PI / 2;
    let q = new THREE.Quaternion().setFromAxisAngle(zAxis, angle);
    matrix.compose(position, q, new THREE.Vector3(1, 1, 1));
    this.SignMatrixs.push(matrix);
  }
  private getAngle(p: THREE.Vector3, center: THREE.Vector3) {
    let xAxis = new THREE.Vector3(1, 0, 0);
    let radiusVector = new THREE.Vector3().subVectors(p, center).normalize();
    let angle = radiusVector.clone().angleTo(xAxis);
    if (radiusVector.clone().cross(xAxis).z > 0) {
      angle = Math.PI * 2 - angle;
    }
    return angle;
  }
  private getArcPoints(startP: THREE.Vector3, endP: THREE.Vector3) {
    if (!this.m_arcOptions) return [];
    let startAngle = this.getAngle(startP, this.m_arcOptions.center);
    let endAngle = this.getAngle(endP, this.m_arcOptions.center);
    let radius = startP.distanceTo(this.m_arcOptions.center);
    let arc = new THREE.ArcCurve(
      this.m_arcOptions.center.x,
      this.m_arcOptions.center.y,
      radius,
      startAngle,
      endAngle,
      this.m_arcOptions.clockwise
    );
    return arc.getPoints(this.m_arcPointsNum);
  }
  private addLines() {
    let addLines: DbLine[] = [];
    if (!this.m_arcOptions) return addLines;
    let key = `${this.m_layer.Id}_${this.m_color}`;
    let lines: DbLine[] = this.m_model.Lines[key]?.lines ?? [];
    let indexs: number[] = [];
    let arcPoints: number[] = [];
    for (let i = 0; i < this.m_arcPointsNum; i++) {
      indexs.push(i);
      if (i != 0 && i != this.m_arcPointsNum - 1) {
        indexs.push(i);
      }
    }
    for (let i = 0; i < this.m_signModelPositions.length - 1; i++) {
      let curPoint = this.m_signModelPositions[i];
      let nextPoint = this.m_signModelPositions[i + 1];
      arcPoints = this.getArcPoints(curPoint, nextPoint).flatMap((item) => [
        item.x,
        item.y,
        0,
      ]);
      let line = new DbLine({
        id: generateUUID(),
        layerId: this.m_layer.Id,
        blockId: "",
        points: new Float32Array(arcPoints),
        indexes: new Int32Array(indexs),
        color: this.m_color,
        extend_id: generateUUID(),
      });
      line.getBoundingBox();
      line.getBoundingSphere();
      line.block_path = generateUUID();
      lines.push(line);
      addLines.push(line);
    }
    if (this.m_model.Lines[key]) {
      this.m_model.Lines[key].lines = lines;
      this.view.m_context.ToolsManager.LinesToModel(
        lines,
        this.m_model.Lines[key].model
      );
    } else {
      this.m_model.Lines[key] = {
        canEdit: true,
        lines: lines,
        model: this.m_model.addLinesToModel(
          this.m_layer.Id,
          this.m_color,
          lines
        ),
      };
    }
    return addLines;
  }
}

export { DrawArcLayout, DrawOperateLineParams };
